import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarEspecialidades,
  crearEspecialidad,
  actualizarEspecialidad,
  eliminarEspecialidad,
} from "../../../../services/especialidadesService";
import { listarNiveles } from "../../../../services/nivelesService";
import { showToast } from "../../../../utils/showToast";

const buildTempId = (() => {
  let counter = 0;
  return () => `temp-especialidad-${counter++}`;
})();

const normalizeEspecialidad = (especialidad = {}, fallbackId) => ({
  id: especialidad.id ?? fallbackId ?? buildTempId(),
  nombre: especialidad.nombre?.trim() || "Sin nombre",
  descripcion: especialidad.descripcion?.trim() || "",
  activo:
    typeof especialidad.activo === "boolean"
      ? especialidad.activo
      : especialidad.activo === 1 || especialidad.activo === "1",
  nivelId:
    especialidad.nivel_requerido_id ??
    especialidad.nivelId ??
    especialidad.nivel_requeridoId ??
    null,
  nivelNombre:
    especialidad.nivel_requerido?.trim() ||
    especialidad.nivelNombre?.trim() ||
    especialidad.nivel_requerido_nombre?.trim() ||
    null,
});

const initialFormState = {
  nombre: "",
  descripcion: "",
  nivel_requerido_id: "",
  activo: true,
};

const normalizeNivel = (nivel = {}) => ({
  id: nivel.id ?? nivel.nivel_id ?? null,
  nombre: nivel.nombre?.trim() || "Sin nombre",
});

const buildPayload = (form) => ({
  nombre: form.nombre?.trim() || "",
  descripcion: form.descripcion?.trim() || null,
  nivel_requerido_id: form.nivel_requerido_id || null,
  activo: form.activo ? 1 : 0,
});

const validarEspecialidad = (form) => {
  if (!form.nombre?.trim()) return "El nombre de la especialidad es obligatorio.";
  return null;
};

const extractEspecialidad = (response, fallback) => {
  const base = response?.especialidad ?? response?.nivel ?? response ?? {};
  const merged = fallback ? { ...fallback, ...base } : base;
  return normalizeEspecialidad(merged, fallback?.id);
};

export default function useEspecialidadesLogic() {
  const [especialidades, setEspecialidades] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nivelesLoading, setNivelesLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [nivelesError, setNivelesError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modal, setModal] = useState("");
  const [especialidadSeleccionada, setEspecialidadSeleccionada] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filtro, setFiltro] = useState("total");

  const resolveNivelNombre = useCallback(
    (nivelId) => {
      if (nivelId === undefined || nivelId === null || nivelId === "") return null;
      const match = niveles.find((nivel) => String(nivel.id) === String(nivelId));
      return match?.nombre ?? null;
    },
    [niveles]
  );

  const fetchEspecialidades = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarEspecialidades();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeEspecialidad);
      setEspecialidades(normalized);
      setListError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setListError(err.message || "No se pudo obtener la lista de especialidades.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNiveles = useCallback(async () => {
    setNivelesLoading(true);
    try {
      const data = await listarNiveles();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeNivel);
      setNiveles(normalized);
      setNivelesError(null);
    } catch (err) {
      console.error(err);
      setNivelesError(err.message || "No se pudo cargar la lista de niveles.");
    } finally {
      setNivelesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEspecialidades();
    fetchNiveles();
  }, [fetchEspecialidades, fetchNiveles]);

  const resumen = useMemo(() => {
    const total = especialidades.length;
    const activas = especialidades.filter((especialidad) => especialidad.activo).length;
    const inactivas = total - activas;
    return { total, activas, inactivas };
  }, [especialidades]);

  const especialidadesFiltradas = useMemo(() => {
    if (filtro === "activas") return especialidades.filter((especialidad) => especialidad.activo);
    if (filtro === "inactivas") return especialidades.filter((especialidad) => !especialidad.activo);
    return especialidades;
  }, [especialidades, filtro]);

  const aplicarFiltro = useCallback((key) => {
    setFiltro((prev) => (prev === key ? "total" : key));
  }, []);

  const abrirModalNuevo = useCallback(() => {
    setFormNuevo(initialFormState);
    setEspecialidadSeleccionada(null);
    setModal("nuevo");
  }, []);

  const abrirModalEditar = useCallback((especialidad) => {
    const normalized = normalizeEspecialidad(especialidad);
    setEspecialidadSeleccionada(normalized);
    setFormEditar({
      nombre: normalized.nombre,
      descripcion: normalized.descripcion,
      nivel_requerido_id: normalized.nivelId || "",
      activo: normalized.activo,
    });
    setModal("editar");
  }, []);

  const abrirModalEliminar = useCallback((especialidad) => {
    setEspecialidadSeleccionada(normalizeEspecialidad(especialidad));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setEspecialidadSeleccionada(null);
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
  }, []);

  const handleCrearEspecialidad = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarEspecialidad(formNuevo);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo);
        const nueva = await crearEspecialidad(payload);
        const fallback = {
          ...formNuevo,
          nivel_requerido_id: formNuevo.nivel_requerido_id || null,
          nivel_requerido: resolveNivelNombre(formNuevo.nivel_requerido_id),
        };
        setEspecialidades((prev) => [...prev, extractEspecialidad(nueva, fallback)]);
        setLastUpdated(new Date());
        showToast({ type: "success", message: "Especialidad creada correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo crear la especialidad.",
        });
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, cerrarModal, resolveNivelNombre]
  );

  const handleActualizarEspecialidad = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!especialidadSeleccionada) return;

      const validation = validarEspecialidad(formEditar);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingEditar(true);
      try {
        const payload = buildPayload(formEditar);
        const actualizado = await actualizarEspecialidad(especialidadSeleccionada.id, payload);
        const fallback = {
          ...especialidadSeleccionada,
          ...formEditar,
          nivel_requerido_id: formEditar.nivel_requerido_id || null,
          nivel_requerido: resolveNivelNombre(formEditar.nivel_requerido_id),
          id: especialidadSeleccionada.id,
        };

        setEspecialidades((prev) =>
          prev.map((especialidad) =>
            especialidad.id === especialidadSeleccionada.id
              ? extractEspecialidad(actualizado, fallback)
              : especialidad
          )
        );
        setLastUpdated(new Date());
        showToast({ type: "success", message: "Especialidad actualizada correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo actualizar la especialidad.",
        });
      } finally {
        setSavingEditar(false);
      }
    },
    [especialidadSeleccionada, formEditar, cerrarModal, resolveNivelNombre]
  );

  const handleEliminarEspecialidad = useCallback(async () => {
    if (!especialidadSeleccionada) return;
    setDeleting(true);
    try {
      await eliminarEspecialidad(especialidadSeleccionada.id);
      setEspecialidades((prev) =>
        prev.filter((especialidad) => especialidad.id !== especialidadSeleccionada.id)
      );
      setLastUpdated(new Date());
      showToast({ type: "success", message: "Especialidad eliminada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo eliminar la especialidad.",
      });
    } finally {
      setDeleting(false);
    }
  }, [especialidadSeleccionada, cerrarModal]);

  return {
    especialidades: especialidadesFiltradas,
    especialidadesFiltradas,
    niveles,
    loading,
    nivelesLoading,
    listError,
    nivelesError,
    resumen,
    filtro,
    aplicarFiltro,
    lastUpdated,
    reload: fetchEspecialidades,
    reloadNiveles: fetchNiveles,
    modal,
    especialidadSeleccionada,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearEspecialidad,
    handleActualizarEspecialidad,
    handleEliminarEspecialidad,
  };
}
